import type React from 'react';
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import {
    getUserLanguage,
    getUserLocale,
    type SupportedLanguage,
} from '../utils/content/languageUtils';
import { logger } from '../utils/core/logger';

interface ChatTheme {
    primaryColor: string;
    siteConfig: { name: string };
    domain: string;
    conversationLanguage?: string; // Language override from parent (e.g., language picker)
}

interface ChatThemeContextType {
    theme: ChatTheme;
}

const ChatThemeContext = createContext<ChatThemeContextType | undefined>(undefined);

interface ChatThemeProviderProps {
    children: ReactNode;
    theme: ChatTheme;
}

export const ChatThemeProvider: React.FC<ChatThemeProviderProps> = ({ children, theme }) => {
    return <ChatThemeContext.Provider value={{ theme }}>{children}</ChatThemeContext.Provider>;
};

export const useChatTheme = (): ChatTheme | null => {
    const context = useContext(ChatThemeContext);
    return context?.theme ?? null;
};

/**
 * Hook to get the effective language for the chat, respecting conversationLanguage prop
 * This uses getUserLanguage which prefers conversationLanguage over auto-detection
 * Falls back to auto-detection if ChatThemeProvider is not available
 */
export const useChatLanguage = (): SupportedLanguage => {
    const theme = useChatTheme();

    if (theme) {
        return getUserLanguage(theme.conversationLanguage, undefined, theme.domain);
    } else {
        // Fallback when ChatThemeProvider is not available
        // This can happen during development or if provider setup is incomplete
        logger.warn('ChatThemeProvider not found, falling back to auto-detection');
        return getUserLanguage(undefined, undefined, undefined);
    }
};

/**
 * Hook to get the effective locale for website content such as CTAs and dynamic questions.
 * This keeps explicit regional variants like "en-gb" when present while still falling back
 * to a base language such as "en" on generic pages.
 */
export const useChatLocale = (): string => {
    const theme = useChatTheme();

    if (theme) {
        return getUserLocale(theme.conversationLanguage, undefined, theme.domain);
    } else {
        logger.warn('ChatThemeProvider not found, falling back to locale auto-detection');
        return getUserLocale(undefined, undefined, undefined);
    }
};
